import { PropertyWithParts } from "@boark/shared/types/property-with-parts";
import { Outlet, Route, Routes, useSearchParams } from "react-router-dom";

import { Button } from "../../components/button";
import { H1 } from "../../components/h1";
import { Column, Filter, Table } from "../../components/table";
import { useGetMaintenancePlans } from "../../queries/maintenance-plan";
import { useGetProperties } from "../../queries/property";
import { useLocale } from "../../stores/locale";
import { recordIdToId } from "../../utils/record-id-to-id";
import { MaintenancePropertyCreate } from "./maintenance-property-create";
import { MaintenancePropertyDelete } from "./maintenance-property-delete";
import { MaintenancePropertyEdit } from "./maintenance-property-edit";

export function MaintenancePropertyList() {
  const { locale: t } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: properties, isLoading: isPropertiesLoading } =
    useGetProperties();
  const { data: maintenancePlans } = useGetMaintenancePlans();

  const usedPropertyIds = new Set(
    maintenancePlans?.map((plan) =>
      recordIdToId(plan.propertyPart.property.id),
    ) ?? [],
  );

  function updateSearchParams(params: Record<string, string>) {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      ...params,
    });
  }

  return (
    <>
      <Routes>
        <Route element={<MaintenancePropertyCreate />} path="/create" />
        <Route element={<MaintenancePropertyEdit />} path="/:id/edit" />
        <Route element={<MaintenancePropertyDelete />} path="/:id/delete" />
      </Routes>
      <Outlet />
      <div className="w-full min-h-full overflow-x-auto">
        <div className="flex justify-between items-center">
          <H1>{t.maintenance.propertiesTab}</H1>
          <Button route="/admin/maintenance/property/create">
            {t.property.create}
          </Button>
        </div>
        <Table
          data={properties || []}
          loading={isPropertiesLoading}
          editRouteButton={(row) =>
            `/admin/maintenance/property/${recordIdToId(row.id)}/edit`
          }
          filterBy={
            JSON.parse(searchParams.get("filterBy") || "[]") as Filter[]
          }
          locale={{ yes: t.yes, no: t.no, actions: t.action.actions, empty: t.empty }}
          removeRouteButton={(row: PropertyWithParts) =>
            usedPropertyIds.has(recordIdToId(row.id))
              ? undefined
              : `/admin/maintenance/property/${recordIdToId(row.id)}/delete`
          }
          sortBy={searchParams.get("sortBy") || "name"}
          sortByDirection={
            searchParams.get("sortByDirection") as "asc" | "desc"
          }
          onFilter={(value: Filter[]) => {
            updateSearchParams({ filterBy: JSON.stringify(value) });
          }}
          onSort={(field, direction) => {
            updateSearchParams({
              sortBy: field,
              sortByDirection: direction,
            });
          }}
        >
          <Column field="name">{t.property.name}</Column>
          <Column
            body={(row: PropertyWithParts) =>
              row.parts.map((part) => part.code).join(", ") || "-"
            }
            field="parts"
          >
            {t.property.parts}
          </Column>
        </Table>
      </div>
    </>
  );
}
